import React, { PropsWithChildren, useCallback, useState } from "react";
import { OptionDTO } from "../data/OptionDTO";
import { MDPartyDetail } from "../data/MDPartyDetail";
import { ModalMode } from "./NexratesCounterpartiesModal";

const InputOrText : React.FC<{
    value: string | undefined,
    isEdit: boolean,
    onNewValue: (newValue: string) => void,
    readOnly?: boolean,
} & React.InputHTMLAttributes<HTMLInputElement>> = React.memo(({value, isEdit, onNewValue, readOnly = false, ...inputProps}) => {
    console.log("Rendering InputOrText");
    if (isEdit) {
        return <div className="col-4"><input type="text" readOnly={readOnly} disabled={readOnly} value={value} className="form-control" onChange={(event) => onNewValue(event.target.value)} {...inputProps} /></div>;
    } else {
        return <div className="col-4">{value}</div>;
    }
});

const InputOrSelect : React.FC<{
    value: string | undefined,
    isEdit: boolean,
    options: OptionDTO[],
    onNewValue: (newValue: string) => void,
    readOnly?: boolean,
}> = React.memo(({value, isEdit, options, onNewValue, readOnly = false}) => {
    console.log("Rendering InputOrSelect");
    if (isEdit) {
        return <div className="col-4"><select value={value} className="form-select" onChange={(event) => onNewValue(event.target.value)}
            disabled={readOnly} >
            {
                options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)
            }
        </select></div>
    } else {
        return <div className="col-4">{options.find(option => option.value === value)?.label}</div>;
    }
});

const MyFormLabel : React.FC<PropsWithChildren> = React.memo(({children}) => {
    console.log("Rerender form label");
    return <div className="col-2 d-flex align-items-center justify-content-end"><strong>{children}</strong></div>;
});

const Asterix = () => <span style={{color: 'red'}}>*</span>

const NexratesCounterpartiesModalData : React.FC<{
    formData : MDPartyDetail,
    sites : OptionDTO[],
    tierLevels: OptionDTO[],
    modalMode: ModalMode,
    onFormChange: <K extends keyof MDPartyDetail>(fieldName : K, newValue : string) => void
}> = ({formData, sites, tierLevels, modalMode, onFormChange}) => {

    console.log("Re-rendering NexratesCounterpartiesModalData");
    
    const isEdit = modalMode === ModalMode.CREATE || modalMode === ModalMode.EDIT;
    const [dirtyMarkets, setDirtyMartkets] = useState<boolean>(false);

    const onMarketFormChange : <K extends keyof MDPartyDetail>(fieldName : K, newValue : string) => void = useCallback((fieldName, newValue) => {
            setDirtyMartkets(true);
            onFormChange(fieldName, newValue);
    }, [onFormChange]);

    const onCodeFormChange : <K extends keyof MDPartyDetail>(fieldName : K, newValue : string) => void = useCallback((fieldName, newValue) => {
        onFormChange(fieldName, newValue);
        if (!dirtyMarkets) {
            onFormChange("tomsCode", newValue);
            onFormChange("tnpCode", newValue);
            onFormChange("maCode", newValue);
            onFormChange("twCode", newValue);
        }
    }, [dirtyMarkets, onFormChange]);

    return <>
    <div className="container">
        <div className="row py-2">
            <div className="col"><h6>General Information</h6></div>
        </div>
        <div className="row py-2">
            <MyFormLabel>Code<Asterix />:</MyFormLabel>
            <InputOrText value={formData.partyCode} isEdit={isEdit} readOnly={modalMode === ModalMode.EDIT} onNewValue={useCallback(val => onCodeFormChange('partyCode', val), [onCodeFormChange])} maxLength={50} />
            <MyFormLabel>Site<Asterix />:</MyFormLabel>
            <InputOrSelect value={formData.siteCode} isEdit={isEdit} readOnly={modalMode === ModalMode.EDIT} options={sites} onNewValue={useCallback(val => onFormChange('siteCode', val), [onFormChange])} />
        </div>
        <div className="row py-2">
            <MyFormLabel>Description<Asterix />:</MyFormLabel>
            <InputOrText value={formData.description} isEdit={isEdit} onNewValue={useCallback(val => onFormChange('description', val), [onFormChange])} maxLength={250} />
        </div>
        <div className="row py-2">
            <MyFormLabel>LEI:</MyFormLabel>
            <InputOrText value={formData.lei} isEdit={isEdit} onNewValue={useCallback(val => onFormChange('lei', val.toUpperCase()), [onFormChange])} maxLength={250} />
        </div>
        <div className="row py-2">
            <MyFormLabel>CCN:</MyFormLabel>
            <InputOrText value={formData.ccn} isEdit={isEdit} onNewValue={useCallback(val => onFormChange('ccn', val), [onFormChange])} maxLength={250} />
        </div>
        <div className="row py-2">
            <MyFormLabel>Default sales book<Asterix />:</MyFormLabel>
            <InputOrText value={formData.defaultSalesBook} isEdit={isEdit} onNewValue={useCallback(val => onFormChange('defaultSalesBook', val), [onFormChange])} maxLength={25} />
        </div>
    </div>
    <div>&nbsp;</div>
    <div className="container">
        <div className="row py-2">
            <div className="col"><h6>Tiering Info</h6></div>
        </div>
        <div className="row py-2">
            <MyFormLabel>Bond<Asterix />:</MyFormLabel>
            <InputOrSelect value={formData.bondTierId} isEdit={isEdit} options={tierLevels} onNewValue={useCallback(val => onFormChange('bondTierId', val), [onFormChange])} />
            <MyFormLabel>IRS<Asterix />:</MyFormLabel>
            <InputOrSelect value={formData.irsTierId} isEdit={isEdit} options={tierLevels} onNewValue={useCallback(val => onFormChange('irsTierId', val), [onFormChange])} />
        </div>
    </div>
    <div>&nbsp;</div>
    <div className="container">
        <div className="row py-2">
            <div className="col"><h6>Market Codes</h6></div>
        </div>
        <div className="row py-2">
            <MyFormLabel>TOMS:</MyFormLabel>
            <InputOrText value={formData.tomsCode} isEdit={isEdit} onNewValue={useCallback(val => onMarketFormChange('tomsCode', val), [onMarketFormChange])} />
            <MyFormLabel>TNP:</MyFormLabel>
            <InputOrText value={formData.tnpCode} isEdit={isEdit} onNewValue={useCallback(val => onMarketFormChange('tnpCode', val), [onMarketFormChange])} />
        </div>
        <div className="row py-2">
            <MyFormLabel>MarketAxess:</MyFormLabel>
            <InputOrText value={formData.maCode} isEdit={isEdit} onNewValue={useCallback(val => onMarketFormChange('maCode', val), [onMarketFormChange])} />
            <MyFormLabel>TW:</MyFormLabel>
            <InputOrText value={formData.twCode} isEdit={isEdit} onNewValue={useCallback(val => onMarketFormChange('twCode', val), [onMarketFormChange])} />
        </div>
        <div className="row py-2">
            <MyFormLabel>Eurex:</MyFormLabel>
            <InputOrText value={formData.eurexCode} isEdit={isEdit} onNewValue={useCallback(val => onMarketFormChange('eurexCode', val), [onMarketFormChange])} />
            <MyFormLabel>MTS:</MyFormLabel>
            <InputOrText value={formData.mtsCode} isEdit={isEdit} onNewValue={useCallback(val => onMarketFormChange('mtsCode', val.toUpperCase()), [onMarketFormChange])} />
        </div>
    </div>
</>
};

export default React.memo(NexratesCounterpartiesModalData);